/**
 * GSAP-powered animations for Approve/Decline buttons in NotificationBell.
 * Modeled after QuickBookModal's vinyl animation but at ~20% intensity.
 *
 * Flow:
 *   1. Dropdown shrinks into the notification item's center → small disc pops in & spins
 *   2. API call runs (disc keeps spinning)
 *   3. Disc disappears → shockwave + particles + result icon (✓ or ✕)
 *   4. Brief hold → cleanup
 */
import { gsap } from 'gsap';
import { Physics2DPlugin } from 'gsap/Physics2DPlugin';

gsap.registerPlugin(Physics2DPlugin);

const DISC_SIZE = 64; // px  (QuickBookModal uses 180)

// ─── Haptic ─────────────────────────────────────────────────

export function triggerHaptic(): void {
  try { navigator?.vibrate?.(15); } catch { /* no-op */ }
}

// ─── Phase 1: Shrink dropdown into item center + pop disc ───

export interface ShrinkContext {
  timeline: gsap.core.Timeline;
  wrapper: HTMLDivElement;
  disc: HTMLDivElement;
  rotationTween: gsap.core.Tween;
  cx: number;
  cy: number;
}

/**
 * Shrinks the dropdown content toward the item's center,
 * then pops in a small spinning disc (like a mini-vinyl).
 *
 * Returns a context object the caller uses to trigger the final poof.
 */
export function startShrinkAndSpin(
  dropdownEl: HTMLElement,
  itemEl: HTMLElement,
  isApprove: boolean,
): ShrinkContext {
  const itemRect = itemEl.getBoundingClientRect();
  const cx = itemRect.left + itemRect.width / 2;
  const cy = itemRect.top + itemRect.height / 2;

  const dropRect = dropdownEl.getBoundingClientRect();

  // Snapshot dropdown to fixed positioning
  gsap.set(dropdownEl, {
    position: 'fixed',
    top: dropRect.top,
    left: dropRect.left,
    width: dropRect.width,
    height: dropRect.height,
    margin: 0,
    zIndex: 9998,
  });

  const tl = gsap.timeline();

  // Phase 1a: Shrink the dropdown to nothing (centered on the item)
  tl.to(dropdownEl, {
    duration: 0.45,
    width: 0,
    height: 0,
    top: cy,
    left: cx,
    borderRadius: '50%',
    padding: 0,
    overflow: 'hidden',
    opacity: 0,
    ease: 'power3.in',
  });

  // Create disc overlay
  const wrapper = createMiniDisc(isApprove);
  const halfDisc = DISC_SIZE / 2;
  gsap.set(wrapper, {
    left: cx - halfDisc,
    top: cy - halfDisc,
    scale: 0,
    opacity: 0,
  });

  const disc = wrapper.querySelector('.notif-disc') as HTMLDivElement;

  // Phase 1b: Pop the disc in
  tl.to(wrapper, {
    duration: 0.35,
    scale: 1,
    opacity: 1,
    ease: 'back.out(1.7)',
  }, '-=0.2');

  // Phase 2: Spin disc with accelerating timeScale
  const rotationTween = gsap.to(disc, {
    rotation: 360,
    duration: 1.5,
    repeat: -1,
    ease: 'none',
    paused: true,
  });
  rotationTween.play();

  tl.to(rotationTween, {
    timeScale: 6,
    duration: 1.2,
    ease: 'power2.in',
  });

  // Slight motion blur
  tl.to(disc, {
    filter: 'blur(1px)',
    duration: 1.2,
    ease: 'power2.in',
  }, '<');

  // Stash rotation tween for cleanup
  (tl as any)._rotationTween = rotationTween;

  return { timeline: tl, wrapper, disc, rotationTween, cx, cy };
}

// ─── Phase 3: Final poof — disc → shockwave + particles + icon ──

/**
 * Explodes the spinning disc into shockwave + particles + result icon.
 * Returns a promise that resolves when the visual sequence is done.
 */
export function triggerFinalPoof(ctx: ShrinkContext, isApprove: boolean): Promise<void> {
  return new Promise((resolve) => {
    const { wrapper, rotationTween, timeline, cx, cy } = ctx;

    // Kill spin
    rotationTween.kill();
    timeline.kill();

    // Hide disc
    gsap.set(wrapper, { opacity: 0 });

    // Shockwave + particles + result icon
    createShockwave(cx, cy, isApprove);
    spawnParticles(cx, cy, isApprove);
    showResultIcon(cx, cy, isApprove);

    // Light screen shake (20% of QuickBookModal's)
    gsap.fromTo('body',
      { x: -2 },
      {
        x: 2,
        duration: 0.04,
        repeat: 4,
        yoyo: true,
        ease: 'none',
        onComplete: () => gsap.set('body', { x: 0 }),
      },
    );

    // Wait for result icon animation to finish, then resolve
    setTimeout(() => {
      wrapper.remove();
      resolve();
    }, 1400);
  });
}

// ─── Cleanup ────────────────────────────────────────────────

/** Kill everything and restore styles if the animation gets interrupted. */
export function cleanupAnimation(ctx: ShrinkContext | null, dropdownEl: HTMLElement | null): void {
  if (ctx) {
    ctx.rotationTween.kill();
    ctx.timeline.kill();
    ctx.wrapper.remove();
  }
  if (dropdownEl) {
    gsap.set(dropdownEl, { clearProps: 'all' });
  }
}

// ─── Internal: mini disc (like vinyl but smaller + colored) ──

function createMiniDisc(isApprove: boolean): HTMLDivElement {
  const S = DISC_SIZE;
  const accent = isApprove ? '#10b981' : '#ef4444';

  const wrapper = document.createElement('div');
  wrapper.style.cssText = `
    position: fixed; z-index: 9999; pointer-events: none;
    width: ${S}px; height: ${S}px;
    display: flex; align-items: center; justify-content: center;
    opacity: 0;
  `;

  const disc = document.createElement('div');
  disc.className = 'notif-disc';
  disc.style.cssText = `
    position: absolute; inset: 0; border-radius: 50%;
    background:
      radial-gradient(circle, #222 6%, transparent 8%),
      radial-gradient(circle, ${accent} 30%, #111 33%),
      conic-gradient(from 0deg,
        #0d0d0d 0deg, #1a1a1a 60deg, #0d0d0d 120deg,
        #1f1f1f 180deg, #0d0d0d 240deg, #1a1a1a 300deg, #0d0d0d 360deg
      );
    box-shadow:
      0 0 0 2px ${accent},
      0 4px 16px rgba(0,0,0,0.5),
      inset 0 0 10px rgba(0,0,0,0.4);
  `;

  // Groove rings
  const grooves = document.createElement('div');
  grooves.style.cssText = `
    position: absolute; inset: 0; border-radius: 50%;
    background: repeating-radial-gradient(
      circle,
      transparent 0px, rgba(255,255,255,0.04) 1px,
      transparent 2px, transparent 4px
    );
    mask-image: radial-gradient(circle, transparent 30%, black 34%, black 93%, transparent 96%);
    -webkit-mask-image: radial-gradient(circle, transparent 30%, black 34%, black 93%, transparent 96%);
  `;
  disc.appendChild(grooves);

  // Center label
  const labelSize = S * 0.55;
  const label = document.createElement('div');
  label.style.cssText = `
    position: absolute; top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: ${labelSize}px; height: ${labelSize}px;
    border-radius: 50%;
    background: radial-gradient(circle, ${accent} 55%, ${isApprove ? '#065f46' : '#7f1d1d'} 100%);
    box-shadow: 0 1px 4px rgba(0,0,0,0.35);
    display: flex; align-items: center; justify-content: center;
  `;

  const icon = document.createElement('div');
  icon.style.cssText = `
    font-size: ${S * 0.18}px; color: #fff; font-weight: bold;
    font-family: var(--font-pixel, monospace);
  `;
  icon.textContent = isApprove ? '✓' : '✕';
  label.appendChild(icon);
  disc.appendChild(label);

  // Spindle hole
  const hole = document.createElement('div');
  hole.style.cssText = `
    position: absolute; top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: ${S * 0.06}px; height: ${S * 0.06}px;
    border-radius: 50%;
    background: radial-gradient(circle, #444 30%, #222 100%);
  `;
  disc.appendChild(hole);

  wrapper.appendChild(disc);
  document.body.appendChild(wrapper);
  return wrapper;
}

// ─── Internal: shockwave rings ──────────────────────────────

function createShockwave(x: number, y: number, isApprove: boolean): void {
  const color = isApprove ? '#10b981' : '#ef4444';

  // Inner ring — status color
  const ring = document.createElement('div');
  ring.style.cssText = `
    position: fixed; z-index: 9999; pointer-events: none;
    border-radius: 50%; width: 20px; height: 20px;
    border: 3px solid ${color};
  `;
  document.body.appendChild(ring);
  gsap.set(ring, { left: x - 10, top: y - 10, opacity: 1 });
  gsap.to(ring, {
    scale: 10,
    opacity: 0,
    borderWidth: 0,
    duration: 0.6,
    ease: 'power2.out',
    onComplete: () => ring.remove(),
  });

  // Outer ring — gold accent
  const ring2 = document.createElement('div');
  ring2.style.cssText = `
    position: fixed; z-index: 9999; pointer-events: none;
    border-radius: 50%; width: 20px; height: 20px;
    border: 2px solid #F4D35E;
  `;
  document.body.appendChild(ring2);
  gsap.set(ring2, { left: x - 10, top: y - 10, opacity: 0.7 });
  gsap.to(ring2, {
    scale: 14,
    opacity: 0,
    borderWidth: 0,
    duration: 0.75,
    delay: 0.06,
    ease: 'power2.out',
    onComplete: () => ring2.remove(),
  });
}

// ─── Internal: particle burst ───────────────────────────────

function spawnParticles(x: number, y: number, isApprove: boolean): void {
  const count = 20; // QuickBookModal uses 70
  const statusColor = isApprove ? '#10b981' : '#ef4444';
  const palette = [statusColor, statusColor, statusColor, '#F4D35E', '#F4D35E', '#CBEDFF', '#000000'];

  for (let i = 0; i < count; i++) {
    const dot = document.createElement('div');
    const size = gsap.utils.random(2, 10);
    const color = palette[Math.floor(Math.random() * palette.length)];
    const isSquare = Math.random() > 0.6;
    dot.style.cssText = `
      position: fixed; z-index: 9999; pointer-events: none;
      width: ${size}px; height: ${size}px;
      border-radius: ${isSquare ? '2px' : '50%'};
      background: ${color};
    `;
    document.body.appendChild(dot);
    gsap.set(dot, { left: x, top: y, rotation: gsap.utils.random(0, 360) });
    gsap.to(dot, {
      physics2D: {
        velocity: gsap.utils.random(200, 550),
        angle: gsap.utils.random(0, 360),
        gravity: 450,
        friction: 0.04,
      },
      opacity: 0,
      rotation: `+=${gsap.utils.random(-180, 180)}`,
      duration: gsap.utils.random(0.6, 1.2),
      onComplete: () => dot.remove(),
    });
  }
}

// ─── Internal: result icon (check / X) ─────────────────────

function showResultIcon(cx: number, cy: number, isApprove: boolean): void {
  const size = 52; // QuickBookModal uses 72
  const icon = document.createElement('div');
  const color = isApprove ? '#10b981' : '#ef4444';
  icon.style.cssText = `
    position: fixed; z-index: 10000; pointer-events: none;
    width: ${size}px; height: ${size}px;
    left: ${cx - size / 2}px; top: ${cy - size / 2}px;
  `;
  icon.innerHTML = isApprove
    ? `<svg viewBox="0 0 52 52" width="${size}" height="${size}">
         <circle cx="26" cy="26" r="24" fill="none" stroke="${color}" stroke-width="2.5"/>
         <path fill="none" stroke="${color}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
       </svg>`
    : `<svg viewBox="0 0 52 52" width="${size}" height="${size}">
         <circle cx="26" cy="26" r="24" fill="none" stroke="${color}" stroke-width="2.5"/>
         <path fill="none" stroke="${color}" stroke-width="4" stroke-linecap="round" d="M17 17l18 18M35 17l-18 18"/>
       </svg>`;
  document.body.appendChild(icon);

  gsap.fromTo(icon,
    { scale: 0, opacity: 0 },
    {
      scale: 1.15,
      opacity: 1,
      duration: 0.5,
      delay: 0.1,
      ease: 'elastic.out(1, 0.5)',
      onComplete: () => {
        gsap.to(icon, {
          opacity: 0,
          scale: 0.8,
          duration: 0.35,
          delay: 0.45,
          onComplete: () => icon.remove(),
        });
      },
    },
  );
}
